import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function SignUp() {
  const { signInWithGoogle, signInWithFacebook } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showProfileStage, setShowProfileStage] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [newUserId, setNewUserId] = useState<string | null>(null);
  const [newUserEmail, setNewUserEmail] = useState<string | null>(null);
  const [profileFormData, setProfileFormData] = useState({
    phone: '',
    dob_day: '',
    dob_month: '',
    dob_year: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError('');
    setSuccessMessage('');

    // Validation
    if (!formData.fullName || !formData.email || !formData.password) {
      setError('All fields are required');
      return;
    }


    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      // Import supabase directly for this signup call
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        import.meta.env.VITE_PUBLIC_SUPABASE_URL || "https://sibpoabenkejwltenabf.supabase.co",
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNpYnBvYWJlbmtlandsdGVuYWJmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMTM0NjgsImV4cCI6MjA4Mzc4OTQ2OH0.9MFPEGIggob9HTdKkVTk7qzDo5ccpUSAdQzop8TRAEY"
      );

      const { data, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          },
          emailRedirectTo: `${window.location.origin}/email-verified`,
        },
      });

      // STEP 1: Check for auth errors FIRST
      if (authError) {
        // Check for duplicate email
        if (
          authError.message.toLowerCase().includes('already') || 
          authError.message.toLowerCase().includes('registered') ||
          authError.message.toLowerCase().includes('exists') ||
          authError.status === 422 ||
          authError.code === 'user_already_exists'
        ) {
          setError('That email is already in use. Please log in.');
          setLoading(false);
          return;
        }
        
        // Generic auth error
        setError(authError.message || 'Failed to create account');
        setLoading(false);
        return;
      }

      // STEP 2: Verify user object exists
      if (!data.user) {
        setError('Failed to create account. Please try again.');
        setLoading(false);
        return;
      }

      // STEP 3: Verify user ID exists
      if (!data.user.id) {
        setError('Account creation failed. Please contact support.');
        setLoading(false);
        return;
      }

      // STEP 4 & 5: Send welcome email and proceed to Stage 2 profile setup
      try {
        await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-welcome-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNpYnBvYWJlbmtlandsdGVuYWJmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMTM0NjgsImV4cCI6MjA4Mzc4OTQ2OH0.9MFPEGIggob9HTdKkVTk7qzDo5ccpUSAdQzop8TRAEY'
          },
          body: JSON.stringify({
            email: formData.email,
            full_name: formData.fullName
          })
        });
      } catch (welcomeErr) {
        console.warn('Welcome email failed:', welcomeErr);
      }

      setNewUserId(data.user.id);
      setNewUserEmail(formData.email);
      setLoading(false);
      setShowProfileStage(true);

    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  const isProfileFormComplete = () => {
    return profileFormData.phone !== '' && profileFormData.dob_day !== '' && profileFormData.dob_month !== '' && profileFormData.dob_year !== '' && profileFormData.address !== '' && profileFormData.suburb !== '' && profileFormData.state !== '' && profileFormData.postcode !== '';
  };

  const handleProfileFieldChange = (field: string, value: string) => {
    setProfileFormData(prev => ({ ...prev, [field]: value }));
    setProfileError('');
  };

  const handleProfileSave = async () => {
    if (!newUserId) return;
    if (!isProfileFormComplete()) {
      setProfileError('Please fill in all required fields');
      return;
    }
    setProfileSaving(true);
    setProfileError('');
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        import.meta.env.VITE_PUBLIC_SUPABASE_URL || "https://sibpoabenkejwltenabf.supabase.co",
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNpYnBvYWJlbmtlandsdGVuYWJmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMTM0NjgsImV4cCI6MjA4Mzc4OTQ2OH0.9MFPEGIggob9HTdKkVTk7qzDo5ccpUSAdQzop8TRAEY"
      );
      const dob = `${profileFormData.dob_year}-${profileFormData.dob_month.padStart(2,'0')}-${profileFormData.dob_day.padStart(2,'0')}`;
      const { error: updateError } = await supabase.rpc('admin_update_profile', {
        p_user_id: newUserId,
        p_phone: profileFormData.phone,
        p_date_of_birth: dob,
        p_street_address: profileFormData.address,
        p_suburb: profileFormData.suburb,
        p_state: profileFormData.state,
        p_postcode: profileFormData.postcode,
        p_country: profileFormData.country,
      });
      if (updateError) throw updateError;
      setProfileComplete(true);
    } catch (err: any) {
      setProfileError('Failed to save profile. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    const { error } = await signInWithGoogle();
    if (error) {
      if (error.message.toLowerCase().includes('already') || 
          error.message.toLowerCase().includes('registered')) {
        setError('That email is already in use. Please log in.');
      } else {
        setError(error.message || 'Failed to sign up with Google');
      }
    }
  };

  const handleFacebookSignUp = async () => {
    setError('');
    const { error } = await signInWithFacebook();
    if (error) {
      if (error.message.toLowerCase().includes('already') || 
          error.message.toLowerCase().includes('registered')) {
        setError('That email is already in use. Please log in.');
      } else {
        setError(error.message || 'Failed to sign up with Facebook');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          {!showProfileStage && (
          <div className="text-center mb-8">
            <img
              src="https://www.stashly.com.au/Stashly-logo.png"
              alt="Stashly"
              className="h-20 mx-auto mb-6"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
            <p className="text-gray-600">Start earning from your spare space in 10 minutes</p>
          </div>
          )}

          {/* Stage 2 — Complete Your Profile */}
          {showProfileStage && !profileComplete && (
            <div className="mt-6">
              <div className="bg-green-50 border-2 border-green-400 rounded-xl p-4 flex items-start gap-3 mb-6">
                <i className="ri-checkbox-circle-fill text-green-600 text-2xl flex-shrink-0"></i>
                <div>
                  <p className="text-green-800 font-bold">You&apos;re in! 🎉</p>
                  <p className="text-green-700 text-sm">Now let&apos;s finish setting up your profile</p>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Complete Your Profile</h2>
              <p className="text-sm text-gray-500 mb-4">This helps build trust with renters and hosts. You can also <button type="button" onClick={() => navigate('/browse')} className="text-[#14B8A6] underline hover:text-[#0F9B8E] cursor-pointer bg-transparent border-none p-0 text-sm">do this later</button>.</p>
              {profileError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600"></i>
                  <p className="text-red-800 text-sm">{profileError}</p>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                  <input type="tel" value={profileFormData.phone} onChange={(e) => handleProfileFieldChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                    placeholder="e.g. 0412 345 678" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" value={profileFormData.dob_day} onChange={(e) => handleProfileFieldChange('dob_day', e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm text-center"
                      placeholder="DD" min="1" max="31" />
                    <input type="number" value={profileFormData.dob_month} onChange={(e) => handleProfileFieldChange('dob_month', e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm text-center"
                      placeholder="MM" min="1" max="12" />
                    <input type="number" value={profileFormData.dob_year} onChange={(e) => handleProfileFieldChange('dob_year', e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm text-center"
                      placeholder="YYYY" min="1900" max="2010" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address <span className="text-red-500">*</span></label>
                  <input type="text" value={profileFormData.address} onChange={(e) => handleProfileFieldChange('address', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm"
                    placeholder="e.g. 12 Smith Street" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Suburb <span className="text-red-500">*</span></label>
                    <input type="text" value={profileFormData.suburb} onChange={(e) => handleProfileFieldChange('suburb', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm"
                      placeholder="e.g. Sydney" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State <span className="text-red-500">*</span></label>
                    <select value={profileFormData.state} onChange={(e) => handleProfileFieldChange('state', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm">
                      <option value="">Select</option>
                      <option value="NSW">NSW</option><option value="VIC">VIC</option>
                      <option value="QLD">QLD</option><option value="WA">WA</option>
                      <option value="SA">SA</option><option value="TAS">TAS</option>
                      <option value="ACT">ACT</option><option value="NT">NT</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Postcode <span className="text-red-500">*</span></label>
                  <input type="text" value={profileFormData.postcode} onChange={(e) => handleProfileFieldChange('postcode', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] text-sm"
                    placeholder="e.g. 2000" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                  <input type="text" value={profileFormData.country} readOnly
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 cursor-not-allowed" />
                </div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <i className="ri-lock-line text-gray-400"></i>
                  Your address is never shared publicly. This helps build trust with other users.
                </p>
                <button onClick={handleProfileSave} disabled={profileSaving || !isProfileFormComplete()}
                  className="w-full py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {profileSaving ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
                  ) : (
                    <><i className="ri-save-line"></i>Save My Profile</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Stage 2 — Profile Complete */}
          {showProfileStage && profileComplete && (
            <div className="mt-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Complete!</h2>
              <p className="text-gray-600 mb-2">Welcome to Stashly!</p>
              <p className="text-gray-500 text-sm mb-6 flex items-center justify-center gap-1">
                <i className="ri-mail-line"></i>
                We&apos;ve sent a confirmation email to {newUserEmail} — please verify when you get a chance
              </p>
              <div className="flex flex-col gap-3">
                <button onClick={() => navigate('/browse')}
                  className="w-full py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer flex items-center justify-center gap-2">
                  <i className="ri-search-line"></i>Browse Spaces
                </button>
                <button onClick={() => navigate('/list-space')}
                  className="w-full py-3 bg-white text-[#14B8A6] font-semibold rounded-lg border-2 border-[#14B8A6] hover:bg-teal-50 transition-colors cursor-pointer flex items-center justify-center gap-2">
                  <i className="ri-home-4-line"></i>List My Space
                </button>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border-2 border-green-400 rounded-xl p-4 flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-600 text-2xl flex-shrink-0"></i>
              <div>
                <p className="text-green-800 font-medium">{successMessage}</p>
                {successMessage.includes('verify') && (
                  <p className="text-green-700 text-sm mt-1">Redirecting to login page...</p>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !showProfileStage && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <i className="ri-error-warning-line text-red-600 text-xl"></i>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          {!showProfileStage && (
          <>
          <div className="flex flex-col gap-3 mb-6">
            <button type="button" onClick={handleGoogleSignUp} disabled={loading || !!successMessage} className="flex items-center justify-center gap-3 px-4 py-4 bg-white border-2 border-red-500 rounded-xl hover:border-red-600 hover:shadow-md transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full">
              <i className="ri-google-fill text-red-500 text-xl"></i>
              <span className="text-gray-800 font-semibold text-base">Continue with Google</span>
            </button>
            <button type="button" onClick={handleFacebookSignUp} disabled={loading || !!successMessage} className="flex items-center justify-center gap-3 px-4 py-4 bg-[#1877F2] border-2 border-[#1877F2] rounded-xl hover:bg-[#166FE5] hover:shadow-md transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed w-full">
              <i className="ri-facebook-fill text-white text-xl"></i>
              <span className="text-white font-semibold text-base">Continue with Facebook</span>
            </button>
          </div>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">Or sign up with email</span></div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  disabled={loading || !!successMessage}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-400"></i>
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={loading || !!successMessage}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none w-5 h-5 flex items-center justify-center">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  disabled={loading || !!successMessage}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Create a password (min 8 characters)"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !!successMessage}
              className="w-full py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Creating Account...
                </>
              ) : successMessage ? (
                <>
                  <i className="ri-checkbox-circle-line"></i>
                  Account Created!
                </>
              ) : (
                <>
                  <i className="ri-user-add-line"></i>
                  Create Account
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-400 mt-3">Free to list · Secure payments · Cancel anytime</p>
            <p className="text-center text-xs text-gray-400 mt-2">By creating an account you agree to our <a href="/terms" className="underline hover:text-gray-600">Terms of Service</a> and <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a></p>
          </form>
          </>

          )}
        </div>
      </div>
    </div>
  );
}
